import { hash } from '@ember/helper';
import { htmlSafe, type SafeString } from '@ember/template';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';

import type { BarData } from '../../../types/components/prism/data-bar.ts';
import {
  COLOR_TRAFFIC_LIGHT_NEUTRAL,
  getCssVariableValue,
} from '../../../utils/css-variable.ts';
import PrismDataBarBlocksDataBarLabel from './blocks/data-bar-label.gts';
import PrismDataBarBlocksDataBarValue from './blocks/data-bar-value.gts';
import PrismDataBarDataBarBasic from './data-bar-basic.gts';

function getStyleForDataBar(targetValue: number): SafeString {
  // Target marker is 3px wide
  let leftOffset = 3;

  // If it's too small it will appear before the data bar, so negate that
  if (targetValue < 3) {
    leftOffset = 0;
  }

  return htmlSafe(
    `left: calc(${Math.min(targetValue, 100)}% - ${leftOffset}px);`,
  );
}

type ValueItem = {
  isHidden?: boolean;
  style: SafeString;
  widthPercentage?: number;
};

interface PrismDataBarDataBarLabelValueTargetSignature {
  Args: {
    backgroundColor?: string;
    data: BarData[];
    direction?: 'regular' | 'reverse';
    isGradient?: boolean;
    isStriped?: boolean;
    rounded?: 'left' | 'right' | 'both';
    targetValue?: number;
    thresholdWidthPercentage?: number;
    valuePosition?: 'top' | 'bottom';
  };
  Blocks: {
    default: [
      {
        label?: WithBoundArgs<
          typeof PrismDataBarBlocksDataBarLabel,
          'color' | 'text' | 'tooltip'
        >;
        value?: WithBoundArgs<
          typeof PrismDataBarBlocksDataBarValue,
          'color' | 'text' | 'tooltip' | 'valueItems'
        >;
      },
    ];
  };
  Element: HTMLDivElement;
}

export default class PrismDataBarDataBarLabelValueTarget extends Component<PrismDataBarDataBarLabelValueTargetSignature> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get backgroundColor() {
    return (
      this.args.backgroundColor ??
      getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL)
    );
  }

  get bottomValueItems(): ValueItem[] {
    if (!this.args.data) {
      return [
        {
          style: htmlSafe(
            `width: 0%;background-color: ${getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL)};`,
          ),
        },
      ];
    }
    return this.args.data
      .filter((barData) => (barData?.widthPercentage ?? 0) !== 0)
      .map((barData) => ({
        style: htmlSafe(`width: ${barData?.widthPercentage ?? 0}%;`),
        widthPercentage: Math.round(barData?.widthPercentage ?? 0),
        isHidden: barData?.isHidden,
      }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dataBarPositionStyle() {
    const { targetValue } = this.args;
    return targetValue ? getStyleForDataBar(targetValue) : '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dataBarThresholdStyle() {
    return htmlSafe(`width: ${this.args.thresholdWidthPercentage ?? 100}%;`);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showBottomValues() {
    return this.args?.valuePosition === 'bottom';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showTarget() {
    return !isEmpty(this.args.targetValue);
  }

  <template>
    <div class="prism-data-bar-label-value-target">
      <div class="label-and-value-container">
        <div class="label-and-value-container__label-slot">
          {{! This div serves the purpose of making the value align to the right even when the label is not yielded.}}
          {{yield (hash label=(component PrismDataBarBlocksDataBarLabel))}}
        </div>
        <div data-test-id="data-bar-value">
          {{#unless this.showBottomValues}}
            {{yield (hash value=(component PrismDataBarBlocksDataBarValue))}}
          {{/unless}}
        </div>
      </div>
      <div class="data-bar-content">
        <PrismDataBarDataBarBasic
          @backgroundColor={{@backgroundColor}}
          @data={{@data}}
          @dataBarThresholdStyle={{this.dataBarThresholdStyle}}
          @direction={{@direction}}
          @isGradient={{@isGradient}}
          @isStriped={{@isStriped}}
          @rounded={{@rounded}}
        />
        {{#if this.showTarget}}
          <div
            class="data-bar-target"
            style={{this.dataBarPositionStyle}}
          ></div>
        {{/if}}
      </div>

      {{#if this.showBottomValues}}
        <div data-test-id="data-bar-value" style={{this.dataBarThresholdStyle}}>
          {{yield
            (hash
              value=(component
                PrismDataBarBlocksDataBarValue
                showBottomValues=this.showBottomValues
                valueItems=this.bottomValueItems
              )
            )
          }}
        </div>
      {{/if}}
    </div>
  </template>
}
